import React, { ReactElement, useContext } from 'react';
import { MetricsTracker, USER_EVENTS } from '@sm/metrics';
import { t, defineMessages } from '@sm/intl';
import { sanitizeString } from '@sm/utils';
import { Immutable, safeRest } from '@wds/utils';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { useClickInProductMessageMutation } from './ClickInProductMessage.generated';
import HeaderContext from '../../HeaderContext';
import useStyles from './useStylesNotificationItem';

export type NotificationItemProps = Immutable<{
  children: string;
  id: string;
  read?: boolean;
  sentAt: string;
  campaignName: string;
}>;

export const COPY = defineMessages({
  TODAY: {
    id: 'NotificationItem.today',
    defaultMessage: 'Today',
    description:
      '[Type: Label][Vis.: high] - Label to indicate a date was today.',
  },
  YESTERDAY: {
    id: 'NotificationItem.yesterday',
    defaultMessage: 'Yesterday',
    description:
      '[Type: Label][Vis.: high] - Label to indicate a date was yesterday.',
  },
  NOW: {
    id: 'NotificationItem.now',
    defaultMessage: 'Just now',
    description:
      '[Type: Label][Vis.: high] - Label to indicate a time occuring within a minute.',
  },
});

const formatRelativeDate = (date: Date, lang: string) => {
  const isToday = (someDate: Date) => {
    const today = new Date();
    return (
      someDate.getDate() === today.getDate() &&
      someDate.getMonth() === today.getMonth() &&
      someDate.getFullYear() === today.getFullYear()
    );
  };

  if (isToday(date)) {
    return t(COPY.TODAY);
  }

  function isYesterday(someDate: Date) {
    if (!(someDate instanceof Date)) {
      throw new Error('Invalid argument: you must provide a "date" instance');
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    return (
      someDate.getDate() === yesterday.getDate() &&
      someDate.getMonth() === yesterday.getMonth() &&
      someDate.getFullYear() === yesterday.getFullYear()
    );
  }

  if (isYesterday(date)) {
    return t(COPY.YESTERDAY);
  }

  return date.toLocaleDateString(lang, { month: 'short', day: 'numeric' });
};

const formatRelativeTime = (time: Date, lang: string) => {
  const timeDiffInSeconds = Math.floor(
    (new Date().getTime() - time.getTime()) / 1000
  );
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });

  if (timeDiffInSeconds < 60) {
    return t(COPY.NOW);
  }

  if (timeDiffInSeconds < 3600) {
    return rtf.format(-Math.floor(timeDiffInSeconds / 60), 'minute');
  }

  if (timeDiffInSeconds < 86400) {
    return rtf.format(-Math.floor(timeDiffInSeconds / 3600), 'hour');
  }

  return new Intl.DateTimeFormat(lang, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(time);
};

const NotificationItem = ({
  children,
  read = false,
  sentAt,
  id,
  campaignName,
  ...rest
}: NotificationItemProps): ReactElement => {
  const { pageName, pageURL, lang } = useContext(HeaderContext);
  const { notificationsItem } = useStyles({ read });
  const [clickInProductMessage] = useClickInProductMessageMutation();

  const trackNotificationsEvent = () => {
    MetricsTracker.track({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        amplitudeEvent: 'notification center interaction',
        interactionType: 'click',
        interactionLocation: 'mark as read',
        notificationCenterMessage: campaignName,
        pageName,
        pageURL,
      },
    });
  };

  const onClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLAnchorElement;
    if (target.tagName !== 'A') {
      return;
    }
    e.preventDefault();
    const href = target.getAttribute('href');

    if (!href) {
      return;
    }

    await clickInProductMessage({
      variables: {
        input: {
          id,
          linkUrl: href,
        },
      },
    });

    trackNotificationsEvent();

    (window as Window).location.assign(href);
  };

  const date = formatRelativeDate(new Date(sentAt), lang);
  const time = formatRelativeTime(new Date(sentAt), lang);

  return (
    <li className={notificationsItem} {...safeRest(rest)}>
      <Box mb={2}>
        <Typography component="p" variant="bodySm" color="darkMuted">
          {date}&nbsp;&nbsp;&nbsp;{time}
        </Typography>
      </Box>
      <Typography weight={!read ? 'medium' : 'regular'}>
        {/* eslint-disable-next-line */}
        <div
          onClick={e => onClick(e)}
          dangerouslySetInnerHTML={{
            __html: sanitizeString(children.toString()),
          }}
        />
      </Typography>
    </li>
  );
};

export default NotificationItem;
export { NotificationItem };
